import { call, delay, put, takeLatest } from 'redux-saga/effects'
import { employerActions } from 'redux/employer/employerSlice'
import {
  onboardingReducers,
  OnboardingStage,
  WorkforceSyncType,
  SectionType,
} from './onboardingSlice'
import { Action } from 'shared/Types/Actions'
import onboardingService from './onboardingService'
import { onboardingActions } from './onboardingActions'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import employerService from 'redux/employer/employerService'

export function* getOnboardingInfo(action: Action<any>) {
  const employerData = action.payload
  if (!employerData.onboarding) {
    yield put(onboardingReducers.setOnboardingFetchingStatus('success'))
    return
  }
  yield put(onboardingReducers.setOnboarding(employerData.onboarding))
  yield put(onboardingReducers.setOnboardingFetchingStatus('success'))
}

export function* updateOnboardingStageSaga(action: Action<OnboardingStage>) {
  const stage = action.payload
  yield call(onboardingService.updateOnboardingStageService, stage)
  yield put(onboardingReducers.setOnboardingStage(stage))
}

export function* updateWorkforceSyncTypeSaga(
  action: Action<WorkforceSyncType>
) {
  const type = action.payload
  yield call(onboardingService.updateWorkforceSyncTypeService, type)
  yield put(onboardingReducers.setWorkforceSyncType(type))
}

export function* createConnectedAccountSaga(action: Action<any>) {
  const { email } = action.payload
  yield call(employerService.createConnectedAccount, email)
  yield put(employerActions.createConnectedAccountSuccess(action.payload))
}

export function* updateWelcomeModalShownSaga(action: Action<boolean>) {
  const welcomeModalShown = action.payload
  yield put(onboardingReducers.setWelcomeModalShown(welcomeModalShown))
}

export function* updateOnboardingSectionStatusSaga(
  action: Action<{
    sectionType: SectionType
    status: boolean
  }>
) {
  const { sectionType, status } = action.payload
  yield call(
    onboardingService.updateOnboardingSectionStatusService,
    sectionType,
    status
  )
  yield put(
    onboardingReducers.setOnboardingSectionStatus({ sectionType, status })
  )
  // Syncing the onboarding sections with the updated status with a few seconds delay
  // All the dependencies are calculated and updated in the backend
  yield delay(2000)
  const onboardingSections = yield call(
    onboardingService.getAllOnboardingSectionsService
  )
  yield put(onboardingReducers.setAllOnboardingSections(onboardingSections))
}

export function* updateIfEmployeeOnboarding(action: Action<boolean>) {
  const profileData = action.payload
  yield put(
    onboardingReducers.setIsEmployeeOnboarding(!profileData.onboardingComplete)
  )
}

export function* updateIsEmployeeOnboardingSaga(action: Action<boolean>) {
  const isEmployeeOnboarding = action.payload
  yield put(onboardingReducers.setIsEmployeeOnboarding(isEmployeeOnboarding))
}

export function* updateJustCompletedOnboardingSaga(action: Action<boolean>) {
  const justCompletedOnboarding = action.payload
  yield put(
    onboardingReducers.setJustCompletedOnboarding(justCompletedOnboarding)
  )
}

export function* setOnboardingFetchingStatusFetchingSaga() {
  yield put(onboardingReducers.setOnboardingFetchingStatus('fetching'))
}

export function* onboardingSaga() {
  // Not navigating until we know we have the onboarding data or null if not found
  yield takeLatest(
    [employerActions.getEmployer.type],
    setOnboardingFetchingStatusFetchingSaga
  )
  // Watching for successful getEmployer and saving the onboarding to slice
  // getEmployerSuccess is dispatched when the employer data is fetched successfully
  yield takeLatest([employerActions.getEmployerSuccess.type], getOnboardingInfo)
  // Watching for successful getEmployee and saving the onboarding to slice
  // getEmployeeSuccess is dispatched when the employee data is fetched successfully
  yield takeLatest(
    [employeePlatformActions.getProfileDataSuccess.type],
    updateIfEmployeeOnboarding
  )
  yield takeLatest(
    [onboardingActions.updateOnboardingStage.type],
    updateOnboardingStageSaga
  )
  yield takeLatest(
    [onboardingActions.updateWorkforceSyncType.type],
    updateWorkforceSyncTypeSaga
  )
  yield takeLatest(
    [onboardingActions.updateWelcomeModalShown.type],
    updateWelcomeModalShownSaga
  )
  yield takeLatest(
    [onboardingActions.updateOnboardingSectionStatus.type],
    updateOnboardingSectionStatusSaga
  )
  yield takeLatest(
    [onboardingActions.updateIsEmployeeOnboarding.type],
    updateIsEmployeeOnboardingSaga
  )
  yield takeLatest(
    [onboardingActions.updateJustCompletedOnboarding.type],
    updateJustCompletedOnboardingSaga
  )
  yield takeLatest(
    [onboardingActions.createConnectedAccount.type],
    createConnectedAccountSaga
  )
}

export default onboardingSaga
